import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { GraduationCap } from "lucide-react";

/* Structured Admission Outcome — the ONLY frontend surface that calls the
   canonical POST /opportunities/{id}/admission-outcome mechanism (backend
   accepted, unchanged). Mirrors ConsultationActions.jsx's dual-mode
   forceOpen/hideTriggerButtons pattern exactly: a stage-gate blocker
   (required_admission_outcome) routes straight into this same dialog
   instead of a generic override prompt, and there is no second,
   frontend-only outcome state anywhere in this component. */
export const AdmissionOutcomeActions = ({ opportunityId, contactName, customerId, onSaved, forceOpen = false, hideTriggerButtons = false }) => {
  const [open, setOpen] = useState(false);
  const [outcome, setOutcome] = useState("");
  const [note, setNote] = useState("");
  const [nextAction, setNextAction] = useState("");
  const [lostReason, setLostReason] = useState("");
  const [closureInitiatedBy, setClosureInitiatedBy] = useState("");
  const [saving, setSaving] = useState(false);

  const openDialog = () => {
    setOpen(true); setOutcome(""); setNote("");
    setNextAction(""); setLostReason(""); setClosureInitiatedBy("");
  };
  // Lets a stage-move blocker (blocker_type "required_admission_outcome")
  // open straight into this dialog, the same way ConsultationActions'
  // own forceOpen already works for required_consultation_outcome.
  useEffect(() => { if (forceOpen) openDialog(); }, [forceOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const reset = () => {
    setOpen(false); setOutcome(""); setNote("");
    setNextAction(""); setLostReason(""); setClosureInitiatedBy("");
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.post(`/opportunities/${opportunityId}/admission-outcome`, {
        outcome,
        note,
        next_action: outcome === "unsuccessful" ? nextAction : null,
        lost_reason: outcome === "unsuccessful" && nextAction === "lost" ? lostReason : null,
        closure_initiated_by: outcome === "unsuccessful" && nextAction === "lost" ? closureInitiatedBy : null,
      });
      const label = outcome === "successful"
        ? "Successful admission recorded — journey progressing"
        : nextAction === "lost"
          ? "Unsuccessful admission recorded — moved to Lost"
          : "Unsuccessful admission recorded — returned to University Selection";
      toast.success(r.data?.note || label);
      reset(); onSaved?.();
    } catch (e) { toast.error(errDetail(e)); } finally { setSaving(false); }
  };

  const disabled = saving
    || !outcome
    || !note.trim()
    || (outcome === "unsuccessful" && !nextAction)
    || (outcome === "unsuccessful" && nextAction === "lost" && !lostReason.trim())
    || (outcome === "unsuccessful" && nextAction === "lost" && !closureInitiatedBy);

  return (
    <>
      {!hideTriggerButtons && (
        <Button data-testid={`admission-outcome-trigger-${opportunityId}`} size="sm" variant="outline" className="h-7 text-xs" onClick={openDialog}>
          <GraduationCap className="h-3.5 w-3.5 mr-1" /> Record Admission Outcome
        </Button>
      )}

      <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">Admission outcome</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {(contactName || customerId) && (
              <div className="rounded-md border bg-muted/40 p-2.5 text-xs space-y-0.5">
                <p><b>{contactName}</b>{customerId ? ` · ${customerId}` : ""}</p>
              </div>
            )}

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Admission decision (mandatory)</Label>
              <div className="grid grid-cols-2 gap-1.5">
                {[["successful", "Successful"], ["unsuccessful", "Unsuccessful"]].map(([v, l]) => (
                  <Button key={v} data-testid={`admission-outcome-${v}-${opportunityId}`} size="sm" variant={outcome === v ? "default" : "outline"} onClick={() => setOutcome(v)}>{l}</Button>
                ))}
              </div>
            </div>

            {outcome === "unsuccessful" && (
              <div className="space-y-2 rounded-md border p-2.5">
                <Label className="text-xs">What happens next (required)</Label>
                <Select value={nextAction} onValueChange={setNextAction}>
                  <SelectTrigger data-testid="admission-outcome-next-action"><SelectValue placeholder="Choose…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="university_selection">Return to University Selection</SelectItem>
                    <SelectItem value="lost">Lost</SelectItem>
                  </SelectContent>
                </Select>
                {nextAction === "lost" && (
                  <>
                    <Input data-testid="admission-outcome-lost-reason" placeholder="Lost reason (required — opportunity history preserved)" value={lostReason} onChange={(e) => setLostReason(e.target.value)} />
                    <Select value={closureInitiatedBy} onValueChange={setClosureInitiatedBy}>
                      <SelectTrigger data-testid="admission-outcome-closure-initiated-by"><SelectValue placeholder="Who decided not to proceed? (required)" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="customer">Customer decided not to proceed</SelectItem>
                        <SelectItem value="aussie_pathways">Aussie Pathways declined this customer</SelectItem>
                        <SelectItem value="mutual_other">Mutual / other</SelectItem>
                      </SelectContent>
                    </Select>
                  </>
                )}
              </div>
            )}

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Note (mandatory)</Label>
              <Textarea data-testid="admission-outcome-note-input" placeholder="Note stored with author + timestamp; visible in the admission history…" value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid={`admission-outcome-save-${opportunityId}`} disabled={disabled} onClick={save}>
              {saving ? "Saving…" : "Save admission outcome"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
